export type ShopCategory = "ship" | "radio" | "home" | "curios";

export interface ShopItem {
  id: string;
  category: ShopCategory;
  name: string;
  cost: number;
  description: string;
  stackable?: boolean;
  maxLevel?: number;
  effect: ShopEffect;
}

export type ShopEffect =
  | { kind: "cadence" }
  | { kind: "drive" }
  | { kind: "secondBarrel" }
  | { kind: "sharperEye" }
  | { kind: "ricochet" }
  | { kind: "forgivingHull" }
  | { kind: "sensorCourtesy" }
  | { kind: "bulletColor"; color: number }
  | { kind: "flareColor"; color: number }
  | { kind: "livery"; color: number }
  | { kind: "unlockChannel"; channelId: string }
  | { kind: "decor" }
  | { kind: "flereHat"; hatKey: string };

export const CATEGORY_LABELS: Record<ShopCategory, string> = {
  ship: "Ship",
  radio: "Radio",
  home: "Home Beat",
  curios: "Curios",
};

export const SHOP_ITEMS: ShopItem[] = [
  // --- Ship fittings (mechanical, aesthetically framed) ---
  {
    id: "cadence",
    category: "ship",
    name: "A crisper cadence",
    cost: 35,
    description: "The shots have more tone.",
    stackable: true,
    maxLevel: 3,
    effect: { kind: "cadence" },
  },
  {
    id: "drive",
    category: "ship",
    name: "A responsive drive",
    cost: 40,
    description: "She turns when you think it.",
    stackable: true,
    maxLevel: 3,
    effect: { kind: "drive" },
  },
  {
    id: "sensor-courtesy",
    category: "ship",
    name: "Sensor courtesy",
    cost: 30,
    description: "The ship notices things.",
    effect: { kind: "sensorCourtesy" },
  },
  {
    id: "sharper-eye",
    category: "ship",
    name: "A sharper eye",
    cost: 45,
    description: "The Ministry's prototype targeting.",
    effect: { kind: "sharperEye" },
  },
  {
    id: "second-barrel",
    category: "ship",
    name: "A second barrel",
    cost: 65,
    description: "Twin guns. For the look of it.",
    effect: { kind: "secondBarrel" },
  },
  {
    id: "forgiving-hull",
    category: "ship",
    name: "A forgiving hull",
    cost: 55,
    description: "The plating takes a knock.",
    stackable: true,
    maxLevel: 3,
    effect: { kind: "forgivingHull" },
  },
  {
    id: "ricochet",
    category: "ship",
    name: "Ricochet rounds",
    cost: 70,
    description: "A little flourish — shots pass cleanly through the first rock.",
    effect: { kind: "ricochet" },
  },

  // --- Ship cosmetics ---
  {
    id: "flare-cyan",
    category: "ship",
    name: "Engine flare — cyan",
    cost: 5,
    description: "A civil hue for the wake.",
    effect: { kind: "flareColor", color: 0x55ccff },
  },
  {
    id: "bullet-gold",
    category: "ship",
    name: "Bullet tint — gold",
    cost: 5,
    description: "Announces itself politely.",
    effect: { kind: "bulletColor", color: 0xe6b44c },
  },
  {
    id: "livery-lavender",
    category: "ship",
    name: "Livery — Lavender Cabaret",
    cost: 45,
    description: "Deep purple hull, unmistakable on the radar.",
    effect: { kind: "livery", color: 0xb27ad9 },
  },

  // --- Radio unlocks ---
  {
    id: "ch-lavender",
    category: "radio",
    name: "Channel: Lavender",
    cost: 40,
    description: "Cabaret signal out of Station 7.",
    effect: { kind: "unlockChannel", channelId: "lavender" },
  },
  {
    id: "ch-retro",
    category: "radio",
    name: "Channel: Retro Hour",
    cost: 35,
    description: "A host who describes the music they cannot play.",
    effect: { kind: "unlockChannel", channelId: "retro" },
  },
  {
    id: "ch-silence",
    category: "radio",
    name: "Channel: Silence Premium",
    cost: 100,
    description: "Sanctified silence. A bureaucratic joke.",
    effect: { kind: "unlockChannel", channelId: "silence" },
  },

  // --- Home beat decor ---
  {
    id: "fern-small",
    category: "home",
    name: "A small fern",
    cost: 8,
    description: "Hardy, unbothered, cooperative.",
    effect: { kind: "decor" },
  },
  {
    id: "rug",
    category: "home",
    name: "A rug",
    cost: 25,
    description: "Handwoven, Station 7 cooperative.",
    effect: { kind: "decor" },
  },

  // --- Curios ---
  {
    id: "compendium",
    category: "curios",
    name: "Ministry Ordinance Compendium, vol. XIV",
    cost: 120,
    description: "A prop for the shelf. Flere will have opinions.",
    effect: { kind: "decor" },
  },

  // --- Flere's hats (cosmetic; Flere wears them in the chat panel) ---
  {
    id: "hat_top",
    category: "curios",
    name: "A top hat for Flere",
    cost: 20,
    description: "Obviously.",
    effect: { kind: "flereHat", hatKey: "hat_top" },
  },
  {
    id: "hat_fez",
    category: "curios",
    name: "A Ministry fez for Flere",
    cost: 20,
    description: "They'll file paperwork, I'll file it in the bin.",
    effect: { kind: "flereHat", hatKey: "hat_fez" },
  },
  {
    id: "hat_beret",
    category: "curios",
    name: "A lavender beret for Flere",
    cost: 20,
    description: "You know my weaknesses.",
    effect: { kind: "flereHat", hatKey: "hat_beret" },
  },
  {
    id: "hat_bicorn",
    category: "curios",
    name: "An admiral's bicorn for Flere",
    cost: 30,
    description: "I was born for this.",
    effect: { kind: "flereHat", hatKey: "hat_bicorn" },
  },
  {
    id: "hat_crown",
    category: "curios",
    name: "A minor coronet for Flere",
    cost: 40,
    description: "A minor coronet, since you asked.",
    effect: { kind: "flereHat", hatKey: "hat_crown" },
  },
  {
    id: "hat_cap",
    category: "curios",
    name: "A Ministry cap for Flere",
    cost: 15,
    description: "Only when we're being watched.",
    effect: { kind: "flereHat", hatKey: "hat_cap" },
  },
];
